import {addDoc, collection, deleteDoc, doc, getDocs, query, updateDoc, where} from 'firebase/firestore';
import {create} from 'zustand';
import {ExerciseSettings, TrainingMode} from '../types/exercises';
import {db} from '../utils/firebase';
import {useAuthStore} from './authStore';

export interface SavedSequence {
  id: string;
  name: string;
  exercises: {
    type: TrainingMode;
    /**
     * Duration in seconds. Use -1 for unlimited duration.
     * For unlimited sessions, exercises must be ended manually.
     */
    duration: number;
    settings?: ExerciseSettings;
  }[];
  createdAt: Date;
  updatedAt: Date;
  userId: string;
}

interface SavedSequencesState {
  savedSequences: SavedSequence[];
  isLoading: boolean;
  error: string | null;
  loadSavedSequences: () => Promise<void>;
  saveSequence: (name: string, exercises: SavedSequence['exercises']) => Promise<void>;
  updateSequence: (id: string, name: string, exercises: SavedSequence['exercises']) => Promise<void>;
  deleteSequence: (id: string) => Promise<void>;
}

export const useSavedSequencesStore = create<SavedSequencesState>(set => ({
  savedSequences: [],
  isLoading: false,
  error: null,

  loadSavedSequences: async () => {
    try {
      set({isLoading: true, error: null});
      const user = useAuthStore.getState().user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const sequencesRef = collection(db, 'savedSequences');
      const q = query(sequencesRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);

      const sequences: SavedSequence[] = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate(),
      })) as SavedSequence[];

      set({savedSequences: sequences, isLoading: false});
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error loading saved sequences';
      set({error: errorMessage, isLoading: false});
    }
  },

  saveSequence: async (name: string, exercises: SavedSequence['exercises']) => {
    try {
      set({isLoading: true, error: null});
      const user = useAuthStore.getState().user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const sequencesRef = collection(db, 'savedSequences');
      const newSequence = {
        name,
        exercises,
        userId: user.uid,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await addDoc(sequencesRef, newSequence);
      await useSavedSequencesStore.getState().loadSavedSequences();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error saving sequence';
      set({error: errorMessage, isLoading: false});
    }
  },

  updateSequence: async (id: string, name: string, exercises: SavedSequence['exercises']) => {
    try {
      set({isLoading: true, error: null});
      const user = useAuthStore.getState().user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const sequenceRef = doc(db, 'savedSequences', id);
      await updateDoc(sequenceRef, {
        name,
        exercises,
        updatedAt: new Date(),
      });
      await useSavedSequencesStore.getState().loadSavedSequences();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error updating sequence';
      set({error: errorMessage, isLoading: false});
    }
  },

  deleteSequence: async (id: string) => {
    try {
      set({isLoading: true, error: null});
      const user = useAuthStore.getState().user;

      if (!user) {
        throw new Error('User not authenticated');
      }

      const sequenceRef = doc(db, 'savedSequences', id);
      await deleteDoc(sequenceRef);
      await useSavedSequencesStore.getState().loadSavedSequences();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error deleting sequence';
      set({error: errorMessage, isLoading: false});
    }
  },
}));
